import {
  email as emailValidation,
  maxLength as maxLengthValidation,
  minLength as minLengthValidation,
  required as requiredValidation,
  runValidations,
} from '@coursera/coursera-ui/lib/utils/validation';

import type { CollectionVariant } from 'bundles/enterprise-admin-collection/types';
import { PROGRAM_NAME_MAX_LENGTH } from 'bundles/enterprise-admin-constants';
import { DESCRIPTION_MAX_LEN, TITLE_MAX_LEN } from 'bundles/enterprise-admin-constants/CollectionAdminConstants';
import { LEARNING_PATH_DESCRIPTION_MAX_LENGTH } from 'bundles/enterprise-admin-learning-path/constants';
import type { Invitee, ValidationErrors } from 'bundles/enterprise-admin-program-creation/types';

import _t from 'i18n!nls/enterprise-admin-program-creation';

const EXTERNAL_ID_MAX_LENGTH = 50; // copied from static/bundles/enterprise-admin-users/components/CreateLearnerInputs.tsx:35

export const validateInvitees = (invitees: Invitee[]): [boolean, ValidationErrors] => {
  const errors: ValidationErrors = {};
  const emailValidations = [requiredValidation, emailValidation];
  const nameValidations = [requiredValidation, minLengthValidation(3)];
  const externalIdValidations = [maxLengthValidation(EXTERNAL_ID_MAX_LENGTH)];
  invitees.forEach(({ name, email, externalId }, index) => {
    errors[`learner-${index}-email`] = runValidations(email, emailValidations) || undefined;
    errors[`learner-${index}-name`] = runValidations(name, nameValidations) || undefined;
    errors[`learner-${index}-external-id`] =
      (externalId && runValidations(externalId, externalIdValidations)) || undefined;
  });

  const hasValidationErrors = Object.keys(errors).some((key) => errors[key]);
  return [hasValidationErrors, errors];
};

const nameExistsValidation = (allProgramNames: string[]) => {
  return (value: string, message = _t('Program name already exists. Please enter a new name.')) => {
    if (allProgramNames.includes(value)) {
      return message;
    }
    return false;
  };
};

export const validateProgramDetails = ({
  name,
  description,
  allProgramNames,
}: {
  name: string;
  description: string;
  allProgramNames: string[];
}): [boolean, ValidationErrors] => {
  const errors: ValidationErrors = {};

  const nameValidations = [
    requiredValidation,
    minLengthValidation(3),
    maxLengthValidation(PROGRAM_NAME_MAX_LENGTH),
    nameExistsValidation(allProgramNames),
  ];
  const descriptionValidations = [maxLengthValidation(90)];
  errors[`program-name`] = runValidations(name, nameValidations) || undefined;
  errors[`program-description`] = runValidations(description, descriptionValidations) || undefined;
  const hasValidationErrors = Object.keys(errors).some((key) => errors[key]);
  return [hasValidationErrors, errors];
};

export const validateCollectionDetails = ({
  name,
  description,
  variant = 'collection',
  isDuplicate,
}: {
  name: string;
  description: string;
  variant?: CollectionVariant;
  isDuplicate?: boolean;
}): [boolean, ValidationErrors] => {
  const errors: ValidationErrors = {};

  const nameValidations = [requiredValidation, minLengthValidation(3), maxLengthValidation(TITLE_MAX_LEN)];
  const descriptionValidations = {
    collection: [maxLengthValidation(DESCRIPTION_MAX_LEN)],
    learningPath: [requiredValidation, maxLengthValidation(LEARNING_PATH_DESCRIPTION_MAX_LENGTH)],
  }[variant];
  errors[`collection-name`] =
    runValidations(name, nameValidations) || (isDuplicate && _t('Name already exists')) || undefined;
  errors[`collection-description`] = runValidations(description, descriptionValidations) || undefined;
  const hasValidationErrors = Object.keys(errors).some((key) => errors[key]);
  return [hasValidationErrors, errors];
};
